import { Divider, Space } from 'antd';
import { generatePath } from 'react-router';

import { Button } from '@atom/button/button';
import { ButtonTypeEnum } from '@atom/button/button.enum';
import { ReadMore } from '@atom/read-more/read-more';
import { ServiceIcon } from '@atom/service-icon/service-icon';
import { Text, Title } from '@atom/typography/typography';
import { ConnectorServiceEnum } from '@enum/connector-service.enum';
import { RouteEnum } from '@enum/route.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { Form } from '@form/common.style';
import { useStoreSelector } from '@hook/store/store.hooks';
import { ConnectorAuthTypeEnum } from '@interface/connector/connector.interface';
import { isExist } from '@util/data/is-exist';

import { IntegrationAuth } from './integration-auth/integration-auth';
import { IntegrationBase } from './integration-base/integration-base';
import { useIntegrationModal } from './integration-modal.hook';
import { ContentWrapper, Modal, TitleWrapper } from './integration-modal.style';
import { IntegrationSaml } from './integration-saml/integration-saml';

interface IntegrationModalProps {
  onClose: () => void;
  serviceId?: ConnectorServiceEnum;
  connectionId?: string;
}

const BASIC_AUTH_TYPES = new Set([ConnectorAuthTypeEnum.Basic, ConnectorAuthTypeEnum.None]);

export const IntegrationModal = ({ serviceId, connectionId, onClose }: IntegrationModalProps) => {
  const isModalOpen = isExist(serviceId);
  const [form] = Form.useForm();
  const connectors = useStoreSelector((state) => state.connectors.data);
  const connector = connectors[serviceId ?? ''];
  const isHibobOauthConnector = connector?.serviceId === ConnectorServiceEnum.HibobOauth;
  const saveIntegration = useIntegrationModal(connector, onClose);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => saveIntegration({ serviceId, ...values }))
      .catch(() => {
        // We just need to prevent the unhandled promise rejection
      });
  };

  return (
    <Modal
      okText='Save'
      open={isModalOpen}
      width='50%'
      footer={
        <Space direction='horizontal'>
          <Button key='back' type={ButtonTypeEnum.Primary} ghost onClick={onClose}>
            Cancel
          </Button>
          <Button key='submit' type={ButtonTypeEnum.Primary} onClick={handleSave}>
            Save
          </Button>
        </Space>
      }
      title={
        isExist(connector) && (
          <TitleWrapper>
            <ServiceIcon height={40} icon={connector.serviceId} width={40} />
            <Title level={4} weight={400}>
              {connector.name}
            </Title>
          </TitleWrapper>
        )
      }
      onCancel={onClose}
      onOk={handleSave}
    >
      <ContentWrapper>
        {isExist(connector) && (
          <>
            <Text color={ColorEnum.Black70} level='m' paragraph>
              {connector.description}
            </Text>
            <ReadMore to={generatePath(RouteEnum.IntegrationView.path, { serviceId })} />
          </>
        )}
        <Divider />
        <IntegrationSaml connectionId={connectionId} connector={connector} />
        {isModalOpen && !isHibobOauthConnector && BASIC_AUTH_TYPES.has(connector.authType) && (
          <IntegrationBase connectionId={connectionId} connector={connector} form={form} />
        )}
        {isModalOpen && (isHibobOauthConnector || connector.authType === ConnectorAuthTypeEnum.OAuth) && (
          <IntegrationAuth connectionId={connectionId} form={form} serviceId={serviceId} />
        )}
      </ContentWrapper>
    </Modal>
  );
};
